// Keeps a login alive.
//
// The server gives two tokens at login: an access token (sent with every
// request, good for 60 minutes) and a refresh token (good for 7 days, used
// only to get a new pair). This module:
//   1. renews the access token shortly BEFORE it runs out, while the app is
//      open (so nobody is cut off in the middle of their work);
//   2. if a request still comes back "401 not logged in", renews once and
//      repeats the request, so the person never notices;
//   3. when renewing is impossible (7 days away, password changed, account
//      removed) clears the login and tells the app, which shows the login
//      page with a clear message instead of failing silently.
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
export const SESSION_EXPIRED_EVENT = 'publishos:session-expired';
export const SESSION_EXPIRED_FLAG = 'publishos_session_expired';

const RENEW_BEFORE_MS = 5 * 60 * 1000;   // renew when under 5 minutes are left
const CHECK_EVERY_MS = 60 * 1000;

const read = (key) => { try { return localStorage.getItem(key); } catch (_) { return null; } };

export const getStoredToken = () => read(ACCESS_KEY);

export const storeTokens = (accessToken, refreshToken) => {
  try {
    if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch (_) { /* storage blocked: cookies still carry the session */ }
};

export const clearTokens = () => {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (_) { /* ignore */ }
};

// Expiry time (ms) written inside a token, or 0 when it cannot be read.
export const tokenExpiry = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(part));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch (_) {
    return 0;
  }
};

// A plain axios without our interceptors: renewing must never trigger itself.
const bare = axios.create();
let renewing = null;

// Get a new token pair. Concurrent callers share one request. Resolves to
// the new access token; rejects with the axios error.
export const refreshSession = () => {
  if (renewing) return renewing;
  const refreshToken = read(REFRESH_KEY);
  renewing = bare
    .post(`${API}/auth/refresh`, refreshToken ? { refresh_token: refreshToken } : {}, { withCredentials: true })
    .then(({ data }) => {
      storeTokens(data.access_token, data.refresh_token);
      return data.access_token;
    })
    .finally(() => { renewing = null; });
  return renewing;
};

const endSession = () => {
  clearTokens();
  try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch (_) { /* ignore */ }
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

// The server said "no" to the refresh token itself (as opposed to the
// network being down, which must not log anyone out).
const refused = (err) => err?.response?.status === 401;

const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout'];
const skipRetry = (url = '') => NO_RETRY.some((p) => url.includes(p));

let installed = false;

export const installSessionKeeper = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => {
      // Any auth answer that carries a new pair (login, switch property,
      // change password) replaces the stored one.
      const url = response.config?.url || '';
      if (url.includes('/auth/') && response.data?.access_token) {
        storeTokens(response.data.access_token, response.data.refresh_token);
      }
      return response;
    },
    async (error) => {
      const config = error.config;
      if (error.response?.status !== 401 || !config || config._sessionRetried || skipRetry(config.url)) {
        throw error;
      }
      if (!getStoredToken() && !read(REFRESH_KEY)) throw error;   // not logged in at all
      config._sessionRetried = true;
      let token;
      try {
        token = await refreshSession();
      } catch (refreshError) {
        if (refused(refreshError)) endSession();
        throw error;
      }
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return axios(config);
    },
  );

  const keepFresh = () => {
    const token = getStoredToken();
    if (!token) return;
    const expiry = tokenExpiry(token);
    if (!expiry || expiry - Date.now() > RENEW_BEFORE_MS) return;
    refreshSession().catch((err) => { if (refused(err)) endSession(); });
  };
  setInterval(keepFresh, CHECK_EVERY_MS);
  // A laptop waking up, or coming back to the tab after lunch.
  window.addEventListener('focus', keepFresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) keepFresh(); });
};
